import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Localized } from "@fluent/react";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Collapse, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  List, ListItem, ListItemAvatar, ListItemText, Paper, Skeleton, Stack,
  Step, StepLabel, Stepper, TextField, Typography,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useGridApiRef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadUsers } from "../../../app/AppDataLoader";
import { ServerErrorMsg } from "../../../app/ServerErrorMsg";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { unknownError } from "../../../app/appTypes";
import type { APIError } from "../../../app/appTypes";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { UsersBrowser } from "../../users/UsersBrowser";
import type { Email } from "../../users/UsersBrowser";
import { selectAllUsers } from "../../users/usersSlice";
import { selectLoggedUser, selectUserCompanyName } from "../../user/userSlice";
import { selectUserByName } from "../../users/usersSlice";
import { loadMailingGroups } from "../../administration/mailingGroups/mailingGroupApi";
import type { MailingGroup, MailingGroupUser } from "../../administration/mailingGroups/mailingGroupApi";
import { createMeeting, getLastMeeting } from "../../project/mettingMinutes/meeting";
import type { Attendee, MeetingAgendaItem } from "../../project/mettingMinutes/meeting";
import { loadMeetingMinutesAgenda } from "../../project/mettingMinutes/meetingMinutesSlice";
import type { MeetingAgendaItemDefinitionDto } from "../../project/mettingMinutes/meetingMinutesSlice";

// Umieść plik w tym samym katalogu co poprzedni komponent.
// DateTimePicker korzysta z istniejącego LocalizationProvider i adaptera Date,
// tak jak w przesłanej wersji. Nie dodawaj drugiego providera w tym pliku.
// Nowe identyfikatory Fluent mogą używać polskiej treści jako fallbacku.
const Text = ({ id, children }: { id: string; children: string }) => (
  <Localized id={id}><span>{children}</span></Localized>
);
const requiredText = <Text id="field-is-required">Pole jest wymagane</Text>;
const normalizeEmail = (value?: string | null) => (value ?? "").trim().toLowerCase();
const isValidDate = (value: Date | null): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());
const isDuplicate = (list: Attendee[], candidate: Attendee) => list.some(item =>
  candidate.email
    ? normalizeEmail(item.email) === normalizeEmail(candidate.email)
    : item.person.trim().toLowerCase() === candidate.person.trim().toLowerCase()
      && item.company.trim().toLowerCase() === candidate.company.trim().toLowerCase()
);
const initials = (name: string) => name.trim().split(/\s+/).filter(Boolean)
  .slice(0, 2).map(part => part[0]).join("").toUpperCase();

type CustomAgendaItem = {
  sharedGuid: string;
  name: string;
  description: string;
};

function SummaryItem({ icon, title, description }: {
  icon: ReactNode; title: ReactNode; description?: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: "text.secondary", display: "flex", pt: 0.25,
        "& .MuiSvgIcon-root": { fontSize: 19 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>{title}</Typography>
        {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
      </Box>
    </Stack>
  );
}

export const CreateMeetingNewVersion = () => {
  const parentTheme = useTheme();
  const theme = useMemo(() => {
    const dark = parentTheme.palette.mode === "dark";
    // Lokalny motyw: kolory poza tym ekranem pozostają zgodne z motywem aplikacji.
    return createTheme(parentTheme, {
      palette: {
        primary: dark
          ? { main: "#8ACDBB", dark: "#6DB9A5", light: "#B9E3D6", contrastText: "#132E26" }
          : { main: "#237E72", dark: "#1B655B", light: "#E3F2ED", contrastText: "#FFFFFF" },
        background: { default: dark ? "#182321" : "#F3F8F7", paper: dark ? "#202E2B" : "#FFFFFF" },
        text: { primary: dark ? "#E7F2EE" : "#203B35", secondary: dark ? "#ADC5BC" : "#627B73" },
        divider: dark ? "#394E47" : "#DCE8E3",
      },
    });
  }, [parentTheme]);

  return (
    <ThemeProvider theme={theme}>
      <LoadUsers component={<Skeleton variant="rounded" height={420} />}>
        <CreateMeetingNewVersionForm />
      </LoadUsers>
    </ThemeProvider>
  );
};

function CreateMeetingNewVersionForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dashboardId = searchParams.get("dashboardId");
  const returnDashboardId = dashboardId && /^[1-9]\d*$/.test(dashboardId) && Number.isSafeInteger(Number(dashboardId))
    ? dashboardId : null;
  const { meetingMinuteId: paramId } = useParams<{ meetingMinuteId: string }>();
  const meetingMinuteId = paramId ?? "";
  const theme = useTheme();
  const softBackground = theme.palette.mode === "dark" ? "#293F37" : "#E3F2ED";
  const loggedUser = useAppSelector(selectLoggedUser);
  const loggedUserCompany = useAppSelector(selectUserCompanyName);
  const user = useAppSelector(state => selectUserByName(state, loggedUser));
  const allUsers = useAppSelector(selectAllUsers);

  const [activeStep, setActiveStep] = useState(0);
  const [title, setTitle] = useState("");
  const [dateAndTime, setDateAndTime] = useState<Date | null>(new Date());
  // Zachowana semantyka endDate z dotychczasowego komponentu.
  // Godzina końca w podsumowaniu jest wyłącznie wyliczonym planem.
  const [endDateAndTime] = useState(new Date());
  const [plannedMeetingTime, setPlannedMeetingTime] = useState<number | "">(60);
  const [location, setLocation] = useState("");
  const [requestedBy, setRequestedBy] = useState("");
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [agendaItems, setAgendaItems] = useState<MeetingAgendaItemDefinitionDto[]>([]);
  // Własne punkty nie zmieniają konfiguracji protokołu.
  const [customAgendaItems, setCustomAgendaItems] = useState<CustomAgendaItem[]>([]);
  const [customAgendaFormOpen, setCustomAgendaFormOpen] = useState(false);
  const [customAgendaName, setCustomAgendaName] = useState("");
  const [customAgendaDescription, setCustomAgendaDescription] = useState("");
  const [validateCustomAgenda, setValidateCustomAgenda] = useState(false);
  const customAgendaNameRef = useRef<HTMLInputElement>(null);
  const [previousAgendaItems, setPreviousAgendaItems] = useState<MeetingAgendaItem[]>([]);
  const [removedPreviousAgendaGuids, setRemovedPreviousAgendaGuids] = useState<string[]>([]);
  const [lastMeetingError, setLastMeetingError] = useState<APIError | null>(null);
  const [lastMeetingRetry, setLastMeetingRetry] = useState(0);
  const [agendaLoading, setAgendaLoading] = useState(Boolean(meetingMinuteId));
  const [agendaError, setAgendaError] = useState<APIError | null>(null);
  const [agendaRetry, setAgendaRetry] = useState(0);
  const [error, setError] = useState<APIError | null>(null);
  const [loadingLastMeeting, setLoadingLastMeeting] = useState(Boolean(meetingMinuteId));
  const [lastMeetingLoaded, setLastMeetingLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveLock = useRef(false);
  const [validateDetails, setValidateDetails] = useState(false);
  const [validatePerson, setValidatePerson] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [manualFormOpen, setManualFormOpen] = useState(false);
  const [appUsersDialogOpen, setAppUsersDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [mailingGroups, setMailingGroups] = useState<MailingGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [attendeeSearch, setAttendeeSearch] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupError, setGroupError] = useState<APIError | null>(null);
  const [groupRetry, setGroupRetry] = useState(0);
  const [person, setPerson] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const firstNameRef = useRef<HTMLInputElement>(null);
  const usersGridRef = useGridApiRef();
  const emails = useMemo(() => new Set<Email>(attendees.map(a => a.email)), [attendees]);
  const selectedGroup = mailingGroups.find(group => group.id === selectedGroupId) ?? null;
  const usersByEmail = useMemo(() =>
    new Map(allUsers.map(person => [normalizeEmail(person.email), person])), [allUsers]);
  const groupMemberToAttendee = (member: MailingGroupUser): Attendee => {
    const knownUser = usersByEmail.get(normalizeEmail(member.email));
    return {
      id: 0,
      person: knownUser
        ? `${knownUser.firstname ?? ""} ${knownUser.surname ?? ""}`.trim() || knownUser.userName
        : `${member.firstName ?? ""} ${member.surname ?? ""}`.trim() || member.email,
      company: knownUser?.companyName ?? "",
      email: member.email.trim(),
      meetingId: 0,
    };
  };
  const missingGroupEmails = selectedGroup?.users.filter(member => !member.email?.trim()).length ?? 0;
  const newGroupAttendees = selectedGroup?.users
    .filter(member => member.email?.trim())
    .map(groupMemberToAttendee)
    .reduce<Attendee[]>((result, member) =>
      isDuplicate(attendees, member) || isDuplicate(result, member) ? result : [...result, member], []) ?? [];
  const attendeeQuery = attendeeSearch.trim().toLocaleLowerCase();
  const visibleAttendees = attendees.map((attendee, index) => ({ attendee, index }))
    .filter(({ attendee }) => `${attendee.person} ${attendee.company} ${attendee.email}`
      .toLocaleLowerCase().includes(attendeeQuery))
    .sort((a, b) => a.attendee.person.localeCompare(b.attendee.person, undefined, { sensitivity: "base" })
      || a.attendee.email.localeCompare(b.attendee.email));

  const durationValid = typeof plannedMeetingTime === "number"
    && Number.isInteger(plannedMeetingTime) && plannedMeetingTime >= 0 && plannedMeetingTime <= 1440;
  const detailsValid = Boolean(title.trim() && location.trim() && isValidDate(dateAndTime) && durationValid);
  const emailValid = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const busy = saving || loadingLastMeeting;
  const hasCustomAgendaDraft = Boolean(customAgendaName.trim() || customAgendaDescription.trim());

  const allAgendaItems = useMemo(() => {
    const activeDefinitions = agendaItems.filter(item => item.isActive);
    const knownGuids = new Set(agendaItems.map(item => item.sharedGuid.toLowerCase()));
    const inheritedItems = [...previousAgendaItems]
      .sort((a, b) => a.order - b.order || a.id - b.id)
      .filter(item => {
        const guid = item.sharedGuid.toLowerCase();
        if (knownGuids.has(guid) || removedPreviousAgendaGuids.includes(guid)) return false;
        knownGuids.add(guid);
        return true;
      });
    const lastOrder = activeDefinitions.reduce((max, item) => Math.max(max, item.order), 0);
    return [
      ...activeDefinitions.map(item => ({ ...item, responsibleUserId: null, isCustom: false, isPrevious: false })),
      ...inheritedItems.map((item, index) => ({
        ...item,
        order: lastOrder + index + 1,
        responsibleUserName: "",
        isCustom: true,
        isPrevious: true,
      })),
      ...customAgendaItems.map((item, index) => ({
        ...item,
        order: lastOrder + inheritedItems.length + index + 1,
        responsibleUserId: null,
        responsibleName: "",
        responsibleEmail: "",
        responsibleUserName: "",
        isCustom: true,
        isPrevious: false,
      })),
    ];
  }, [agendaItems, customAgendaItems, previousAgendaItems, removedPreviousAgendaGuids]);

  useEffect(() => {
    setCustomAgendaItems([]);
    setRemovedPreviousAgendaGuids([]);
    setCustomAgendaFormOpen(false);
    setCustomAgendaName("");
    setCustomAgendaDescription("");
    setValidateCustomAgenda(false);
  }, [meetingMinuteId]);

  useEffect(() => {
    if (!meetingMinuteId) { setLoadingLastMeeting(false); return; }
    let active = true;
    setLoadingLastMeeting(true);
    setLastMeetingLoaded(false);
    setPreviousAgendaItems([]);
    setLastMeetingError(null);
    setError(null);
    dispatch(getLastMeeting(meetingMinuteId)).then(unwrapResult).then(last => {
      if (!active || !last) return;
      setTitle(last.name ?? "");
      setLocation(last.place ?? "");
      setRequestedBy(last.onDemand ?? "");
      setAttendees(last.attendanceList ?? []);
      setPreviousAgendaItems(last.agendaItems ?? []);
      setPlannedMeetingTime(last.plannedMeetingTime > 0 ? last.plannedMeetingTime : 60);
      setLastMeetingLoaded(true);
    }).catch((err: APIError) => { if (active) setLastMeetingError(err); })
      .finally(() => { if (active) setLoadingLastMeeting(false); });
    return () => { active = false; };
  }, [dispatch, meetingMinuteId, lastMeetingRetry]);

  useEffect(() => {
    if (!meetingMinuteId) { setAgendaLoading(false); setAgendaItems([]); return; }
    let active = true;
    setAgendaLoading(true);
    setAgendaError(null);
    dispatch(loadMeetingMinutesAgenda(meetingMinuteId)).then(result => {
      if (!active) return;
      if (loadMeetingMinutesAgenda.fulfilled.match(result)) {
        setAgendaItems([...result.payload]
          .sort((a, b) => a.order - b.order || a.id - b.id));
      } else {
        setAgendaItems([]);
        setAgendaError(result.payload ?? unknownError());
      }
      setAgendaLoading(false);
    });
    return () => { active = false; };
  }, [dispatch, meetingMinuteId, agendaRetry]);

  useEffect(() => {
    if (!groupDialogOpen) return;
    let active = true;
    setLoadingGroups(true);
    setGroupError(null);
    dispatch(loadMailingGroups()).then(result => {
      if (!active) return;
      if (loadMailingGroups.fulfilled.match(result)) {
        setMailingGroups(result.payload);
      } else {
        setGroupError(result.payload ?? unknownError());
      }
    }).finally(() => { if (active) setLoadingGroups(false); });
    return () => { active = false; };
  }, [dispatch, groupDialogOpen, groupRetry]);

  const ensureCurrentUser = () => {
    if (!user) return;
    const attendee: Attendee = {
      id: 0, person: `${user.firstname ?? ""} ${user.surname ?? ""}`.trim(),
      company: loggedUserCompany ?? "", email: user.email, meetingId: 0,
    };
    setAttendees(current => isDuplicate(current, attendee) ? current : [...current, attendee]);
  };

  const handleNext = () => {
    if (busy) return;
    if (activeStep === 0) {
      setValidateDetails(true);
      if (!detailsValid) return;
      ensureCurrentUser();
    }
    if (activeStep === 1 && attendees.length === 0) {
      dispatch(showError("meeting-minutes-error-empty-list"));
      return;
    }
    setActiveStep(step => Math.min(step + 1, 2));
  };

  const handleAddAttendee = () => {
    setValidatePerson(true);
    setEmailTouched(true);
    if (!person.trim() || !company.trim() || !emailValid) return;
    const attendee: Attendee = { id: 0, person: person.trim(), company: company.trim(),
      email: email.trim(), meetingId: 0 };
    if (isDuplicate(attendees, attendee)) {
      dispatch(showError("meeting-minutes-error-user-already-exists"));
      return;
    }
    setAttendees(current => [...current, attendee]);
    setPerson(""); setCompany(""); setEmail("");
    setValidatePerson(false); setEmailTouched(false);
    firstNameRef.current?.focus();
  };

  const handleAddAppUsers = () => {
    const selected: Attendee[] = [];
    usersGridRef.current?.getSelectedRows().forEach(row => selected.push({
      id: 0, person: `${row.firstname ?? ""} ${row.surname ?? ""}`.trim(),
      company: row.companyName ?? "", email: row.email ?? "", meetingId: 0,
    }));
    setAttendees(current => selected.reduce<Attendee[]>(
      (result, attendee) => isDuplicate(result, attendee) ? result : [...result, attendee], current));
    setAppUsersDialogOpen(false);
  };

  const handleAddGroupMembers = () => {
    if (!selectedGroup || newGroupAttendees.length === 0) return;
    setAttendees(current => newGroupAttendees.reduce<Attendee[]>(
      (result, attendee) => isDuplicate(result, attendee) ? result : [...result, attendee], current));
    setGroupDialogOpen(false);
    setSelectedGroupId(null);
  };

  const handleCancelCustomAgendaItem = () => {
    if (busy || saveLock.current) return;
    setCustomAgendaFormOpen(false);
    setCustomAgendaName("");
    setCustomAgendaDescription("");
    setValidateCustomAgenda(false);
  };

  const handleAddCustomAgendaItem = () => {
    if (busy || saveLock.current || !meetingMinuteId || agendaLoading || agendaError) return;
    setValidateCustomAgenda(true);
    const name = customAgendaName.trim();
    if (!name) {
      customAgendaNameRef.current?.focus();
      return;
    }
    // GUID powstaje raz przy dodawaniu, a nie przy renderowaniu lub zapisie.
    const item: CustomAgendaItem = {
      sharedGuid: crypto.randomUUID(),
      name,
      description: customAgendaDescription.trim(),
    };
    setCustomAgendaItems(current => [...current, item]);
    handleCancelCustomAgendaItem();
  };

  const handleRemoveCustomAgendaItem = (sharedGuid: string) => {
    if (busy || saveLock.current) return;
    setCustomAgendaItems(current => current.filter(item => item.sharedGuid !== sharedGuid));
    setRemovedPreviousAgendaGuids(current => [...current, sharedGuid.toLowerCase()]);
  };

  const handleCreateMeeting = async () => {
    if (saveLock.current || busy || !meetingMinuteId || agendaLoading || agendaError || lastMeetingError) return;
    // Nie pomijamy tekstu wpisanego w formularzu, ale jeszcze niedodanego do listy.
    if (hasCustomAgendaDraft) {
      setActiveStep(2);
      setCustomAgendaFormOpen(true);
      setValidateCustomAgenda(true);
      customAgendaNameRef.current?.focus();
      return;
    }
    setValidateDetails(true);
    if (!detailsValid || !isValidDate(dateAndTime)) { setActiveStep(0); return; }
    if (attendees.length === 0) {
      dispatch(showError("meeting-minutes-error-empty-list")); setActiveStep(1); return;
    }
    saveLock.current = true;
    setSaving(true);
    setError(null);
    try {
      const previousAgendaByGuid = new Map(previousAgendaItems.map(item => [item.sharedGuid.toLowerCase(), item]));
      const newId = await dispatch(createMeeting({
        meetingMinuteId,
        meeting: {
          id: 0, name: title.trim(), startDate: dateAndTime.toISOString(),
          endDate: endDateAndTime.toISOString(), place: location.trim(),
          onDemand: requestedBy.trim(), meetingMinuteId, createdBy: "", createdByFullName: "",  
          plannedMeetingTime: Number(plannedMeetingTime), attendanceList: attendees,
          agendaItems: allAgendaItems.map(item => {
            const previous = previousAgendaByGuid.get(item.sharedGuid.toLowerCase());
            return {
              id: 0, sharedGuid: item.sharedGuid, meetingId: 0, name: item.name,
              description: item.description, order: item.order, responsibleUserId: item.responsibleUserId,
              responsibleName: item.responsibleName, responsibleEmail: item.responsibleEmail,
              discussionStatus: "NOT_DISCUSSED", 
              rating: previous?.rating ?? "NOT_ASSESSED",
              summary: previous?.summary ?? null,
              decision: null, reviewedAt: null, reviewedBy: null,
            };
          }),
        },
      })).then(unwrapResult);
      dispatch(showSuccess("saved"));
      navigate(returnDashboardId
        ? `/operational-dashboards/view/${returnDashboardId}`
        : `/operational-dashboards/meeting-minutes/edit/${encodeURIComponent(newId)}`, { replace: true });
    } catch (err) {
      setError(err as APIError);
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  const plannedEnd = isValidDate(dateAndTime) && durationValid
    ? new Date(dateAndTime.getTime() + Number(plannedMeetingTime) * 60_000) : null;
  const timeFormat: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  const sameDay = isValidDate(dateAndTime) && plannedEnd
    ? dateAndTime.toDateString() === plannedEnd.toDateString() : true;
  const timeSummary = isValidDate(dateAndTime) && plannedEnd
    ? `${dateAndTime.toLocaleTimeString(undefined, timeFormat)}–${plannedEnd.toLocaleTimeString(undefined, timeFormat)}${sameDay ? "" : ` (${plannedEnd.toLocaleDateString()})`} · ${plannedMeetingTime} min`
    : "—";
  const stepLabels = [
    <Text id="meeting-minutes-step-general-info">Szczegóły</Text>,
    <Text id="meeting-minutes-step-attendees">Uczestnicy</Text>,
    <Text id="operational-dashboard-agenda-step">Agenda</Text>,
  ];
  const sectionHeader = (heading: ReactNode, description?: ReactNode) => (
    <Box sx={{ mb: 3 }}>
      <Typography component="div" variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{heading}</Typography>
      {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ bgcolor: "background.default", color: "text.primary", borderRadius: 3,
        p: { xs: 2, sm: 3, md: 4 },
        "& .MuiButton-root": { textTransform: "none", borderRadius: 2, boxShadow: "none" },
        "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
        "& .MuiFormHelperText-root": { mx: 0 },
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: -0.4, mb: 0.75 }}>
          <Text id="meeting-create-heading">Przygotuj spotkanie</Text>
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <Text id="meeting-create-description">Uzupełnij szczegóły, sprawdź uczestników i agendę.</Text>
        </Typography>
        <Box sx={{ mt: 2 }}><ServerErrorMsg err={error} /></Box>
        {lastMeetingError && <Stack spacing={1} sx={{ mt: 2 }}>
          <ErrorContainer error={lastMeetingError} />
          <Button onClick={() => setLastMeetingRetry(value => value + 1)} disabled={loadingLastMeeting} sx={{ alignSelf: "flex-start" }}>
            <Text id="meeting-create-previous-retry">_Ponów pobieranie poprzedniego spotkania</Text>
          </Button>
        </Stack>}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 3,
          "& .MuiStepConnector-line": { borderColor: "divider" },
          "& .MuiStepLabel-label.Mui-active": { color: "text.primary", fontWeight: 600 },
          "& .MuiStepIcon-root": { color: "divider" },
          "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: "primary.main" },
          "& .MuiStepIcon-text": { fill: "text.primary" },
          "& .MuiStepIcon-root.Mui-active .MuiStepIcon-text": { fill: "primary.contrastText" },
        }}>
          {stepLabels.map((label, index) => <Step key={index}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 250px" }, gap: 3, alignItems: "start" }}>
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", minWidth: 0 }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: { md: 420 } }}>
              {loadingLastMeeting ? <Stack spacing={2} aria-busy="true">
                <Typography variant="body2" color="text.secondary"><Text id="meeting-create-loading-last">Wczytywanie danych z poprzedniego spotkania…</Text></Typography>
                {[0, 1, 2, 3].map(index => <Skeleton key={index} variant="rounded" height={56} />)}
              </Stack> : <>
                {activeStep === 0 && <>
                  {sectionHeader(<Text id="meeting-create-details">Szczegóły spotkania</Text>,
                    <Text id="meeting-create-details-description">Podstawowe informacje i planowany termin.</Text>)}
                  <Stack spacing={2.5}>
                    <TextField required fullWidth label={<Text id="meeting-minutes-meeting-title">Tytuł spotkania</Text>}
                      value={title} onChange={e => setTitle(e.target.value)}
                      error={validateDetails && !title.trim()} helperText={validateDetails && !title.trim() ? requiredText : undefined} />
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.5fr) minmax(150px, 1fr)" }, gap: 2 }}>
                      <DateTimePicker label={<Text id="meeting-create-date-time">Data i godzina spotkania</Text>}
                        value={dateAndTime} onChange={setDateAndTime} views={["year", "month", "day", "hours", "minutes"]}
                        slotProps={{ textField: { fullWidth: true, required: true,
                          error: validateDetails && !isValidDate(dateAndTime),
                          helperText: validateDetails && !isValidDate(dateAndTime)
                            ? <Text id="field-invalid-date">Data jest nieprawidłowa</Text> : undefined } }} />
                      <TextField fullWidth type="number" label={<Text id="meeting-minutes-meeting-planned-time">Planowany czas (minuty)</Text>}
                        value={plannedMeetingTime} onChange={e => setPlannedMeetingTime(e.target.value === "" ? "" : Number(e.target.value))}
                        inputProps={{ min: 0, max: 1440, step: 1 }} error={validateDetails && !durationValid}
                        helperText={validateDetails && !durationValid ? <Text id="meeting-create-duration-error">Podaj liczbę minut od 0 do 1440.</Text> : undefined} />
                    </Box>
                    <TextField required fullWidth label={<Text id="meeting-minutes-meeting-place">Miejsce spotkania</Text>}
                      value={location} onChange={e => setLocation(e.target.value)}
                      error={validateDetails && !location.trim()} helperText={validateDetails && !location.trim() ? requiredText : undefined} />
                    <TextField fullWidth label={<Text id="meeting-minutes-on-demand">Na żądanie</Text>}
                      value={requestedBy} onChange={e => setRequestedBy(e.target.value)}
                      helperText={<Text id="meeting-create-optional">Opcjonalnie — osoba lub dział.</Text>} />
                  </Stack>
                </>}

                {activeStep === 1 && <>
                  {sectionHeader(<Stack direction="row" spacing={1} alignItems="center"><Text id="meeting-minutes-attendees">Uczestnicy</Text>
                    <Chip size="small" label={attendees.length} sx={{ bgcolor: softBackground, color: "text.primary" }} /></Stack>,
                    <Text id="meeting-create-attendees-description">Sprawdź listę i dodaj osoby, które wezmą udział w spotkaniu.</Text>)}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                    <Button variant="outlined" startIcon={<GroupsOutlinedIcon />} onClick={() => setAppUsersDialogOpen(true)}>
                      <Text id="meeting-create-from-catalogue">Z listy użytkowników</Text>
                    </Button>
                    <Button variant="outlined" startIcon={<GroupAddOutlinedIcon />} onClick={() => setGroupDialogOpen(true)}>
                      <Text id="meeting-create-from-group">_Z grupy mailingowej</Text>
                    </Button>
                    <Button startIcon={manualFormOpen ? <CloseIcon /> : <AddIcon />} onClick={() => setManualFormOpen(open => !open)} aria-expanded={manualFormOpen}>
                      {manualFormOpen ? <Text id="meeting-create-hide-form">Zamknij formularz</Text> : <Text id="meeting-create-add-manually">Dodaj ręcznie</Text>}
                    </Button>
                  </Stack>
                  <Collapse in={manualFormOpen} onEntered={() => firstNameRef.current?.focus()}>
                    <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: softBackground }}>
                      <Stack spacing={2}>
                        <TextField required fullWidth size="small" inputRef={firstNameRef} value={person} onChange={e => setPerson(e.target.value)}
                          label={<Text id="meeting-minutes-attendee-fullname">Imię i nazwisko</Text>}
                          error={validatePerson && !person.trim()} helperText={validatePerson && !person.trim() ? requiredText : undefined} />
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                          <TextField required fullWidth size="small" value={company} onChange={e => setCompany(e.target.value)}
                            label={<Text id="meeting-minutes-attendee-company">Firma</Text>}
                            error={validatePerson && !company.trim()} helperText={validatePerson && !company.trim() ? requiredText : undefined} />
                          <TextField fullWidth size="small" type="email" label="Email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setEmailTouched(true)}
                            error={emailTouched && !emailValid} helperText={emailTouched && !emailValid ? <Text id="meeting-create-email-error">Nieprawidłowy adres e-mail</Text> : undefined} />
                        </Box>
                        <Button variant="contained" onClick={handleAddAttendee} startIcon={<AddIcon />} sx={{ alignSelf: "flex-start" }}>
                          <Text id="meeting-create-add-attendee">Dodaj uczestnika</Text>
                        </Button>
                      </Stack>
                    </Box>
                  </Collapse>
                  {attendees.length === 0 ? <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    <GroupsOutlinedIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="body2"><Text id="meeting-minutes-no-attendees">Brak uczestników</Text></Typography>
                  </Box> : <>
                    <TextField fullWidth size="small" value={attendeeSearch}
                      onChange={event => setAttendeeSearch(event.target.value)}
                      label={<Text id="meeting-create-search-attendees">_Szukaj uczestnika po imieniu, firmie lub e-mailu</Text>}
                      sx={{ mb: 1 }} />
                    <List disablePadding sx={{ maxHeight: "min(400px, 45dvh)", overflowY: "auto" }}>
                    {visibleAttendees.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        <Text id="meeting-create-no-matching-attendees">_Brak uczestników pasujących do wyszukiwania.</Text>
                      </Typography>
                    )}
                    {visibleAttendees.map(({ attendee, index }) => {
                      const isMe = Boolean(user?.email) && normalizeEmail(attendee.email) === normalizeEmail(user?.email);
                      return <ListItem key={`${attendee.email}-${attendee.person}-${index}`} divider sx={{ px: 0, py: 1.5, pr: 7 }}
                        secondaryAction={isMe ? <Chip label={<Text id="meeting-create-you">Ty</Text>} size="small" sx={{ bgcolor: softBackground }} />
                          : <IconButton aria-label={`Usuń: ${attendee.person}`} onClick={() => setAttendees(current => current.filter((_, i) => i !== index))}>
                            <DeleteOutlineIcon fontSize="small" /></IconButton>}>
                        <ListItemAvatar sx={{ minWidth: 48 }}><Avatar sx={{ width: 36, height: 36, fontSize: 13, bgcolor: softBackground, color: "primary.main" }}>
                          {initials(attendee.person)}</Avatar></ListItemAvatar>
                        <ListItemText primary={attendee.person} secondary={[attendee.company, attendee.email].filter(Boolean).join(" · ")}
                          primaryTypographyProps={{ variant: "body2", fontWeight: 500, sx: { overflowWrap: "anywhere" } }}
                          secondaryTypographyProps={{ variant: "caption", sx: { overflowWrap: "anywhere" } }} />
                      </ListItem>;
                    })}
                    </List>
                  </>}
                </>}

                {activeStep === 2 && <>
                  {sectionHeader(<Text id="meeting-create-agenda-heading">Agenda spotkania</Text>)}
                  {!meetingMinuteId ? <Alert severity="error"><Text id="operational-dashboard-agenda-missing-meeting-minutes">Brak identyfikatora protokołu potrzebnego do pobrania agendy.</Text></Alert>
                    : agendaError ? <Stack spacing={2}><ErrorContainer error={agendaError} />
                      <Button onClick={() => setAgendaRetry(value => value + 1)} sx={{ alignSelf: "flex-start" }}><Text id="operational-dashboard-agenda-retry">Ponów pobieranie</Text></Button></Stack>
                    : agendaLoading ? <Stack spacing={2}>{[0, 1, 2].map(index => <Skeleton key={index} variant="rounded" height={72} />)}</Stack>
                    : !allAgendaItems.length ? <Alert severity="info"><Text id="meeting-create-agenda-empty-custom">Agenda jest pusta. Możesz dodać własny punkt poniżej.</Text></Alert>
                    : <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
                      {allAgendaItems.map(item => {
                        const responsible = item.responsibleName || item.responsibleUserName || item.responsibleEmail;
                        return <Box component="li" key={item.sharedGuid} sx={{ display: "flex", gap: 2, py: 2, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
                          <Box sx={{ flexShrink: 0, minWidth: 32, height: 32, px: 0.5, borderRadius: 1.5, display: "grid", placeItems: "center", bgcolor: softBackground, color: "primary.main", fontSize: 13 }}>
                            {String(item.order).padStart(2, "0")}</Box>
                          <Box sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}>
                            <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={1}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                              {item.isCustom && <Chip size="small" variant="outlined"
                                label={item.isPrevious
                                  ? <Text id="meeting-create-agenda-previous-badge">_Z poprzedniego spotkania</Text>
                                  : <Text id="meeting-create-agenda-custom-badge">Własny punkt</Text>}
                                sx={{ bgcolor: softBackground, borderColor: "divider", height: 22, fontSize: 11 }} />}
                            </Stack>
                            {item.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{item.description}</Typography>}
                            {responsible && <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, color: "text.secondary" }}>
                              <GroupsOutlinedIcon sx={{ fontSize: 15 }} /><Typography variant="caption"><Text id="meeting-create-responsible">Odpowiedzialny:</Text> {responsible}</Typography>
                            </Stack>}
                          </Box>
                          {item.isCustom && <IconButton size="small" disabled={busy}
                            aria-label={`Usuń punkt agendy: ${item.name}`}
                            onClick={() => handleRemoveCustomAgendaItem(item.sharedGuid)}
                            sx={{ alignSelf: "flex-start" }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>}
                        </Box>;
                      })}
                    </Box>}
                  {meetingMinuteId && !agendaLoading && !agendaError && <Box sx={{ mt: 2 }}>
                    {!customAgendaFormOpen && <Button variant="outlined" disabled={busy}
                      startIcon={<AddIcon />} onClick={() => setCustomAgendaFormOpen(true)}
                      aria-expanded={customAgendaFormOpen} aria-controls="custom-agenda-form">
                      <Text id="meeting-create-agenda-add-custom">Dodaj własny punkt</Text>
                    </Button>}
                    <Collapse in={customAgendaFormOpen}
                      onEntered={() => customAgendaNameRef.current?.focus()}>
                      <Box id="custom-agenda-form" sx={{ p: 2, borderRadius: 2, bgcolor: softBackground }}>
                        <Stack spacing={2}>
                          <Typography variant="subtitle2">
                            <Text id="meeting-create-agenda-custom-heading">Nowy punkt agendy</Text>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <Text id="meeting-create-agenda-custom-scope">Punkt zostanie dodany tylko do tego spotkania.</Text>
                          </Typography>
                          <TextField required fullWidth size="small" disabled={busy}
                            inputRef={customAgendaNameRef} value={customAgendaName}
                            onChange={event => setCustomAgendaName(event.target.value)}
                            onKeyDown={event => {
                              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                                event.preventDefault();
                                handleAddCustomAgendaItem();
                              }
                            }}
                            label={<Text id="meeting-create-agenda-custom-name">Nazwa punktu</Text>}
                            error={validateCustomAgenda && !customAgendaName.trim()}
                            helperText={validateCustomAgenda && !customAgendaName.trim() ? requiredText : undefined} />
                          <TextField fullWidth multiline minRows={2} size="small" disabled={busy}
                            value={customAgendaDescription}
                            onChange={event => setCustomAgendaDescription(event.target.value)}
                            label={<Text id="meeting-create-agenda-custom-description">Opis (opcjonalnie)</Text>} />
                          {hasCustomAgendaDraft && <Typography variant="caption" color="text.secondary" role="status">
                            <Text id="meeting-create-agenda-custom-pending">Przed rozpoczęciem spotkania dodaj wpis do agendy albo anuluj jego dodawanie.</Text>
                          </Typography>}
                          <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                            <Button variant="contained" disabled={busy} startIcon={<AddIcon />}
                              onClick={handleAddCustomAgendaItem}>
                              <Text id="meeting-create-agenda-custom-confirm">Dodaj do agendy</Text>
                            </Button>
                            <Button color="inherit" disabled={busy} onClick={handleCancelCustomAgendaItem}>
                              <Text id="cancel">Anuluj</Text>
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </Collapse>
                  </Box>}
                </>}
              </>}
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" gap={1} sx={{ p: 2 }}>
              <Button color="inherit" disabled={saving} onClick={() => navigate(-1)} sx={{ color: "text.secondary" }}><Text id="cancel">Anuluj</Text></Button>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {activeStep > 0 && <Button disabled={busy} color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(step => step - 1)}>
                  <Text id="meeting-create-back">Wstecz</Text></Button>}
                {activeStep < 2 ? <Button variant="contained" disabled={busy || !meetingMinuteId} onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                  <Text id="meeting-minutes-next">Dalej</Text></Button>
                  : <Button variant="contained" onClick={handleCreateMeeting} disabled={busy || !meetingMinuteId || agendaLoading || Boolean(agendaError) || Boolean(lastMeetingError) || hasCustomAgendaDraft}
                    startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <CheckIcon />}>
                    {saving ? <Text id="meeting-create-saving">Zapisywanie…</Text> : <Text id="meeting-create-start">Rozpocznij spotkanie</Text>}
                  </Button>}
              </Stack>
            </Stack>
          </Paper>

          <Box component="aside" sx={{ p: { xs: 1, md: 2 }, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}><Text id="meeting-create-summary">Twoje spotkanie</Text></Typography>
            <Typography sx={{ fontWeight: 600, mt: 1, mb: 3, overflowWrap: "anywhere" }}>{title || <Text id="meeting-create-new">Nowe spotkanie</Text>}</Typography>
            <Stack spacing={2.5}>
              <SummaryItem icon={<CalendarMonthOutlinedIcon />} title={isValidDate(dateAndTime)
                ? dateAndTime.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "—"} description={timeSummary} />
              <SummaryItem icon={<PlaceOutlinedIcon />} title={location || "—"} description={<Text id="meeting-minutes-meeting-place">Miejsce spotkania</Text>} />
              <SummaryItem icon={<GroupsOutlinedIcon />} title={<><Text id="meeting-minutes-attendees">Uczestnicy</Text>: {attendees.length}</>} />
              <SummaryItem icon={<FormatListBulletedIcon />} title={<><Text id="operational-dashboard-agenda-step">Agenda</Text>: {agendaLoading || agendaError ? "—" : allAgendaItems.length}</>}
                description={allAgendaItems.some(item => item.isCustom)
                  ? <Text id="meeting-create-agenda-source-inherited">_Konfiguracja protokołu i dodatkowe punkty. Punkty z poprzedniego spotkania można usunąć z tej listy.</Text>
                  : <Text id="meeting-create-agenda-source">Z konfiguracji protokołu</Text>} />
            </Stack>
            {lastMeetingLoaded && <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary"><Text id="meeting-create-copied-notice">Dane poprzedniego spotkania zostały uzupełnione. Sprawdź je przed rozpoczęciem.</Text></Typography>
            </Box>}
          </Box>
        </Box>
        {!meetingMinuteId && <Alert severity="error" sx={{ mt: 2 }}><Text id="operational-dashboard-agenda-missing-meeting-minutes">Brak identyfikatora protokołu potrzebnego do pobrania agendy.</Text></Alert>}
      </Box>

      <Dialog open={appUsersDialogOpen} onClose={() => setAppUsersDialogOpen(false)} fullWidth maxWidth="lg"
        PaperProps={{ sx: { height: "min(720px, calc(100dvh - 32px))" } }}>
        <DialogTitle><Text id="meeting-create-users">Użytkownicy</Text></DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
            <UsersBrowser gridRef={usersGridRef} disabled={emails} hideDisabled />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setAppUsersDialogOpen(false)}><Text id="cancel">Anuluj</Text></Button>
          <Button variant="contained" onClick={handleAddAppUsers}><Text id="meeting-create-add-selected">Dodaj wybranych</Text></Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: "action.hover" }}>
          <Text id="meeting-create-from-group">_Z grupy mailingowej</Text>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {groupError && (
              <>
                <ServerErrorMsg err={groupError} />
                <Button onClick={() => setGroupRetry(value => value + 1)} sx={{ alignSelf: "flex-start" }}>
                  <Text id="retry">_Ponów</Text>
                </Button>
              </>
            )}
            {loadingGroups ? <Skeleton variant="rounded" height={56} /> : !groupError && (
              <Autocomplete options={mailingGroups} value={selectedGroup}
                onChange={(_, group) => setSelectedGroupId(group?.id ?? null)}
                getOptionLabel={group => group.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={<Text id="meeting-create-no-groups">_Brak grup mailingowych</Text>}
                renderInput={params => <TextField {...params}
                  label={<Text id="meeting-create-select-group">_Wybierz grupę mailingową</Text>} />} />
            )}
            {selectedGroup && !groupError && !loadingGroups && (
              <>
                {missingGroupEmails > 0 && (
                  <Alert severity="warning">
                    <Text id="meeting-create-group-missing-email">_Nie można dodać członków grupy bez adresu e-mail.</Text>
                  </Alert>
                )}
                <Typography variant="subtitle2">
                  <Text id="meeting-create-group-members">_Osoby do dodania</Text> ({newGroupAttendees.length})
                </Typography>
                {newGroupAttendees.length === 0 ? (
                  <Alert severity="info">
                    <Text id="meeting-create-group-no-new-members">_Brak nowych osób do dodania z tej grupy.</Text>
                  </Alert>
                ) : (
                  <List dense sx={{ maxHeight: 300, overflowY: "auto" }}>
                    {newGroupAttendees.map(member => (
                      <ListItem key={normalizeEmail(member.email)}>
                        <ListItemText primary={member.person}
                          secondary={[member.company, member.email].filter(Boolean).join(" · ")} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setGroupDialogOpen(false)}>
            <Text id="cancel">_Anuluj</Text>
          </Button>
          <Button variant="contained" disabled={loadingGroups || !!groupError || newGroupAttendees.length === 0}
            onClick={handleAddGroupMembers}>
            <Text id="meeting-create-add-group-members">_Dodaj osoby z grupy</Text>
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
